import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import apiClient from '@/shared/lib/apiClient'

// Mirrors Web::Dashboard::TasksController::PER_INSTRUMENT_LIMIT: cards per instrument shown
// without a click. "Show more" loads everything past it.
export const PER_INSTRUMENT_LIMIT = 50

// Start loading a group's tail about one screen before it scrolls into view.
const NEAR_VIEWPORT_MARGIN = '100% 0px'

/** True while the element is within about one screen of the viewport. */
export const useNearViewport = (ref: RefObject<Element | null>): boolean => {
  const [isNear, setIsNear] = useState(() => typeof IntersectionObserver === 'undefined')

  useEffect(() => {
    const element = ref.current
    if (!element || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(([entry]) => setIsNear(entry.isIntersecting), {
      rootMargin: NEAR_VIEWPORT_MARGIN,
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref])

  return isNear
}

interface InstrumentTailOptions<T> {
  instrumentId: number
  /** First cards embedded in the page props (server-side INITIAL_PER_INSTRUMENT). */
  initialRecords: T[]
  /** Every card the instrument has in this list. */
  total: number
  path: string
  /** The page's applied list filters, so the tail continues the same filtered list. */
  filterParams: Record<string, unknown>
  enabled: boolean
}

interface TailRequest<T> {
  initialRecords: T[]
  showMoreClicks: number
  filterParams: Record<string, unknown>
}

interface LoadedTail<T> {
  records: T[]
  filterParams: Record<string, unknown>
}

interface InstrumentTail<T> {
  records: T[]
  /** Cards still loading for the first time; rendered as placeholders to hold the layout. */
  placeholderCount: number
  /** Cards past PER_INSTRUMENT_LIMIT (or a failed load), offered behind "Show more". */
  remainingCount: number
  loadingMore: boolean
  showMore: () => void
}

/**
 * Loads one instrument's cards past the embedded ones, up to PER_INSTRUMENT_LIMIT, once the
 * group is enabled (near the viewport). A new `initialRecords` array (status poll / action
 * reload) marks the tail stale: the old tail stays on screen, deduplicated against the new
 * initial cards, until the refetch lands.
 */
export const useInstrumentTail = <T extends { id: number }>({
  instrumentId,
  initialRecords,
  total,
  path,
  filterParams,
  enabled,
}: InstrumentTailOptions<T>): InstrumentTail<T> => {
  const [tail, setTail] = useState<LoadedTail<T> | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [showMoreClicks, setShowMoreClicks] = useState(0)
  const [loadedRequest, setLoadedRequest] = useState<TailRequest<T> | null>(null)
  const [loading, setLoading] = useState(false)
  const inFlightRequestRef = useRef<TailRequest<T> | null>(null)

  const autoTailSize = Math.max(Math.min(total, PER_INSTRUMENT_LIMIT) - initialRecords.length, 0)
  const hasTail = expanded ? total > initialRecords.length : autoTailSize > 0
  const request = useMemo<TailRequest<T>>(
    () => ({ initialRecords, showMoreClicks, filterParams }),
    [initialRecords, showMoreClicks, filterParams],
  )

  useEffect(() => {
    if (!enabled || !hasTail || loadedRequest === request) return
    if (inFlightRequestRef.current === request) return

    inFlightRequestRef.current = request
    setLoading(true)
    apiClient
      .get<T[]>(path, {
        params: {
          ...filterParams,
          instrument_id: instrumentId,
          offset: initialRecords.length,
          ...(expanded ? {} : { limit: autoTailSize }),
        },
      })
      .then((response) => {
        if (inFlightRequestRef.current === request)
          setTail({ records: response.data, filterParams })
      })
      .catch((error: unknown) => {
        console.error('[Dashboard] Failed to load instrument cards:', error)
      })
      .finally(() => {
        if (inFlightRequestRef.current !== request) return
        inFlightRequestRef.current = null
        setLoadedRequest(request)
        setLoading(false)
      })
  }, [
    enabled,
    hasTail,
    loadedRequest,
    request,
    path,
    filterParams,
    instrumentId,
    initialRecords,
    expanded,
    autoTailSize,
  ])

  // A tail loaded under other filters belongs to another list: drop it instead of showing it.
  const tailRecords = tail?.filterParams === filterParams ? tail.records : null
  const records = useMemo(() => {
    if (!hasTail || !tailRecords) return initialRecords
    const initialIds = new Set(initialRecords.map((record) => record.id))
    return [...initialRecords, ...tailRecords.filter((record) => !initialIds.has(record.id))]
  }, [hasTail, initialRecords, tailRecords])

  const awaitingTail = hasTail && !tailRecords && (loading || loadedRequest === null)
  const showMore = useCallback(() => {
    setExpanded(true)
    setShowMoreClicks((clicks) => clicks + 1)
  }, [])

  return {
    records,
    placeholderCount: awaitingTail ? autoTailSize : 0,
    remainingCount: loadedRequest && !awaitingTail ? Math.max(total - records.length, 0) : 0,
    loadingMore: loading && showMoreClicks > (loadedRequest?.showMoreClicks ?? 0),
    showMore,
  }
}
